'use client';

import * as React from 'react';
import Cookies from 'js-cookie';
import { usePathname, useRouter } from 'next/navigation';
import { cultivPath } from '../site';
import { GlobeIcon, ChevronDownIcon } from './icons';
import { Cap } from './Cap';

export const LANGS: [string, string][] = [
  ['fr', 'FR'],
  ['nl', 'NL'],
  ['en', 'EN'],
];

/** Swap ONLY the locale segment, preserving the sub-path.
 *  (Avoids the double-slash bug in the shared components/LanguageSwitcher.) */
function useSetLang(locale: string) {
  const router = useRouter();
  const pathname = usePathname() || cultivPath(locale);
  return React.useCallback(
    (l: string) => {
      if (l === locale) return;
      Cookies.set('preferredLocale', l, { expires: 365 });
      const parts = pathname.split('/'); // ['', 'fr', ...]
      if (parts.length > 1) parts[1] = l;
      router.push(parts.join('/') || cultivPath(l));
    },
    [locale, pathname, router]
  );
}

/** Footer/login variant: all three codes inline, current one filled. */
export function LangSwitchInline({ locale, className }: { locale: string; className?: string }) {
  const setLang = useSetLang(locale);
  return (
    <div className={'b-lang' + (className ? ' ' + className : '')}>
      {LANGS.map(([code, lbl]) => (
        <button
          key={code}
          className={'b-lang-btn' + (locale === code ? ' on' : '')}
          aria-current={locale === code ? 'true' : undefined}
          onClick={() => setLang(code)}
        >
          <Cap>{lbl}</Cap>
        </button>
      ))}
    </div>
  );
}

/** Nav variant: globe + current code, opening a small popover. */
export function LangSwitchMenu({ locale, label }: { locale: string; label: string }) {
  const [open, setOpen] = React.useState(false);
  const setLang = useSetLang(locale);
  const ref = React.useRef<HTMLDivElement>(null);
  const current = LANGS.find(([c]) => c === locale)?.[1] ?? locale.toUpperCase();

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="b-navlang" ref={ref}>
      <button
        className={'b-navlang-btn' + (open ? ' open' : '')}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <GlobeIcon size={16} />
        <Cap>{current}</Cap>
        <ChevronDownIcon size={13} className="chev" />
      </button>
      {open && (
        <div className="b-navlang-pop" role="menu">
          {LANGS.map(([code, lbl]) => (
            <button
              key={code}
              role="menuitem"
              className={locale === code ? 'on' : undefined}
              aria-current={locale === code ? 'true' : undefined}
              onClick={() => {
                setOpen(false);
                setLang(code);
              }}
            >
              <Cap>{lbl}</Cap>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
